import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, FormControl, TextField, Button, Container } from '@mui/material';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import AttachIcon from '@mui/icons-material/AttachFile';
import { uniqueId } from 'lodash';
import filesize from "filesize";
import { map as asyncMap } from 'p-iteration';
import QuestionsList from '../Components/QuestionsList';
import ServiceOldActionsPanel, { IOldAction } from '../../../components/ServiceOldActionsPanel/ServiceOldActionsPanel';
import { RootState } from '../../../store/types';
import { i18n } from '../../../translate/i18n';
import Upload from '../../../UI/Upload';
import FileList, { IFile } from '../../../UI/FileList';
import confirm from '../../../UI/Confirm/confirm';
import api from '../../../axios/api';
import processUpload from '../../../utils/processUpload';
import resizeFile from '../../../utils/imageResizer';
import * as actions from '../../../store/actions';
import Signature from './Signature';
import Spinner from '../../../components/Spinner';
import { IActionSigned, IServiceQuestion } from '../../../models/iserviceQuestion';

type IQuestionEdited = {
  _id: string,
  questionsId: string,
  name: string,
  groupName: string,
  type: string,
  answer: string,
  // actions: IActionSigned[],
  comment: string,
  commentFlag: boolean,
  fileFlag: boolean,
  files: string[],
  weight: number,
  questionHadPendingActions: boolean,
}

export interface IEditServiceBody {
  date: Date,
  place: string,
  equipment: string,
  description: string,
  equipmentService: string,
  questions: IQuestionEdited[],
  files: string[],
  expirationDate: Date | null,
}

type Props = {
  goBack: CallableFunction,
  editMode: boolean,
  setEditMode: CallableFunction,
  goToActionPage: (actionId: string) => void,
}

const Checklist: React.FC<Props> = ({ goBack, editMode, setEditMode, goToActionPage }) => {
  const dispatch = useDispatch();
  const [uploadSubmitError, setUploadSubmitError] = useState<boolean>(false);
  const [fileUploadError, setFileUploadError] = useState<boolean>(false);
  
  const questionTree = useSelector((state: RootState) => state.editService.questionsTree);
  const questionsLoading = useSelector((state: RootState) => state.editService.questionsLoading);
  const oldActions = useSelector((state: RootState) => state.editService.oldActions);
  const uploadedFiles = useSelector((state: RootState) => state.editService.files);
  const tenant = useSelector((state: any) => state.auth.selectedAccess.tenant);
  const dateServiceForm = useSelector((state: RootState) => state.editService.date);
  const postServiceLoading = useSelector((state:RootState) => state.editService.postServiceLoading);
  const selectedService = useSelector((state: RootState) => state.editService.selectedService);
  const equipmentId = selectedService.equipment._id;
  const equipmentServiceId = selectedService.equipmentService._id;
  const checklistScore = useSelector((state: RootState) => state.editService.checklistScore);
  const oldActionsWithoutQuestion = useSelector((state: RootState) => state.editService.selectedService.oldActions);
  const serviceExpirationDate = useSelector((state: RootState) => state.editService.expirationDate);
  const description = useSelector((state: RootState) => state.editService.description);

  const updateService = (body: IEditServiceBody): any => dispatch(actions.editServiceUpdateService(selectedService._id, body))

  const questionsRef:any[] = [];

  const handleUpload = async (files: File[]) => {
    const uploadingFiles = await asyncMap(files, async (file) => {
      const fileOptimized = await resizeFile(file)

      return ({
        file: fileOptimized,
        id: uniqueId(),
        name: fileOptimized.name,
        readableSize: filesize(fileOptimized.size),
        preview: URL.createObjectURL(fileOptimized),
        fileType: fileOptimized.type,
        progress: 0,
        isNotUploaded: true,
        error: false,
        url: null
      } as IFile);
    });

    setUploadSubmitError(false);
    setFileUploadError(false);

    dispatch(actions.editServiceAddObservationFile(uploadingFiles));
    uploadingFiles.forEach((file) => processUpload(file, updateFile, setUploadSubmitError));
  };

  const updateFile = (fileId: string, file: any) => {
    dispatch(actions.editServiceEditObservationFile(file, fileId));
  };

  const retryUpload = async (file: IFile) => {
    updateFile(file.id as string, {error: false})
    await processUpload(file, updateFile, setUploadSubmitError);
  }

  const handleDeleteServiceFile = (fileId: string) => {
    confirm(i18n.t('handleDeleteDocumentFileFormService.confirm'),
      {
        title: i18n.t('handleDeleteDocumentFileFormService.title'),
        titleColor: 'red',
        disableBackdrop: true
      }).then(
        () => { },
        async () => {
          await handleDeleteFile(fileId)
        },
      )
  }
  
  const handleDeleteFile = async (fileId: string) => {
    try {
      setUploadSubmitError(false);
      setFileUploadError(false);
      if (fileId.length === 24) await api.delete(`files/${fileId}`)
      dispatch(actions.editServiceDeleteObservationFile(fileId));
    } catch (e) {
      console.log('e', e);
    }
  }

  const getValidUnansweredQuestionsRefs = () => questionsRef.filter(ref => ref.current !== null);

  const scrollToFirstUnansweredQuestion = () => {
    const validUnansweredQuestionsRefs = getValidUnansweredQuestionsRefs();    
    return validUnansweredQuestionsRefs[0]!.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  const handleSubmit = () => {
    let hasError = false;

    const notUploaded = [];
    const withError = [];
    uploadedFiles.forEach(file => {
      if (file.error) withError.push(file);
      else if(file.isNotUploaded) notUploaded.push(file);
    })

    if (notUploaded.length > 0) {
      setUploadSubmitError(true)
      hasError = true;
    }

    if (withError.length > 0) {
      setFileUploadError(true)
      hasError = true;
    }

    const filesIds = uploadedFiles.map(fl => fl.id ? fl.id : fl._id);

    const validUnansweredQuestionsRefs = getValidUnansweredQuestionsRefs();   
    if (validUnansweredQuestionsRefs.length > 0) {
      return scrollToFirstUnansweredQuestion();
    }
    
    const questionsArray: IQuestionEdited[] = []    
    if (hasError) return;
    questionTree.forEach(group => {
      const groupEdited: IQuestionEdited = {
        _id: group.id,
        questionsId: group.id,
        name: group.name,
        groupName: "",
        type: "group",
        answer: "NA",
        // actions: [],
        comment: "",
        commentFlag: false,
        fileFlag: false,
        files: [],
        weight: 1,
        questionHadPendingActions: false,
      }
      questionsArray.push(groupEdited);
      group.questionArray.forEach((question: IServiceQuestion) => {
        const questionFiles = question.files;
        if (questionFiles?.some((file) => file.error)) {
          setFileUploadError(true)
          hasError = true;
        } else if (questionFiles?.some((file) => file.isNotUploaded)) {
          setUploadSubmitError(true)
          hasError = true;
        }
        const questionEdited = {
          _id: question._id,
          questionsId: question._id,
          name: question.name,
          groupName: question.groupName as string,
          type: question.type,
          answer: question.answer,
          // actions: question.actions || [],
          comment: question.comment || '',
          commentFlag: question.commentFlag || false,
          fileFlag: question.fileFlag || false,
          files: questionFiles?.map((file) => file.id ? file.id as string : file._id as string) || [],
          weight: question.weight || 0,
          questionHadPendingActions: false,
        }

        questionEdited.questionHadPendingActions = oldActions.some((action) => {
          const questionId = question._id;
          return questionId === action.question;
        }) || question.actions?.length > 0;
        questionsArray.push(questionEdited) 
      })
    })
  
    if (hasError) return;
    
    const body: IEditServiceBody = {
      date: dateServiceForm,
      place: selectedService.place._id,
      description,
      questions: questionsArray,
      files: filesIds as string[],
      equipmentService: equipmentServiceId,
      equipment: equipmentId,
      expirationDate: serviceExpirationDate
    }

    return updateService(body).then((result: string) => {
      if (result === 'success') {
        setEditMode(false);
      }
    })
  }

  return <Box sx={{position: 'relative'}}>
    {(postServiceLoading || questionsLoading ) && <Spinner overlay size={30} />}

    {!questionsLoading &&
      <QuestionsList
        isNewData={false}
        editMode={editMode}
        questionsRefs={questionsRef}
        checklistScore={checklistScore.toString()}
        mandatoryActionsForNotComplyQuestions={selectedService.equipmentService.mandatoryActionsForNotComplyQuestions}
        questionTree={questionTree}
        serviceId={selectedService._id}
        goToActionPage={goToActionPage}
        createOrUpdateDraft={async() =>{}}
      />
    }
    { oldActionsWithoutQuestion.length > 0 && 
      <>
        <Box sx={{marginTop: '20px', display: 'flex', marginBottom: '10px'}}>
          <PendingActionsIcon sx={{marginRight: '5px'}} />
          <Typography style={{ color: '#000' }}>
            {i18n.t('serviceOldActionsPanel.title2') as string}
          </Typography>
        </Box>
        <ServiceOldActionsPanel actions={oldActionsWithoutQuestion} showRefreshButton={false}/>
      </>
    }
    <Box style={styles.sectionTitleContainer}>
      <AttachIcon style={{ color: !editMode ? 'lightgrey' : '#000' }} />
      <Typography style={{ color: !editMode ? 'lightgrey' : '#000' }}>{i18n.t('typographyFormService.attachFiles') as string}</Typography>
    </Box>
    {editMode && <Upload onUpload={handleUpload} />}
    {!!uploadedFiles.length && (
      <>
        <FileList
          files={uploadedFiles}
          onDelete={handleDeleteServiceFile}
          disabled={false}
          retry={(file: IFile) => retryUpload(file)}
        />
        {uploadSubmitError && <Box sx={styles.fileError}>{i18n.t('BoxFormService.fileError') as string}</Box>}
      </>
    )}
    <FormControl variant="standard" fullWidth>
      <TextField
        variant="standard"
        disabled={!editMode}
        label={i18n.t('textFieldFormService.description') as string}
        name='obs'
        onChange={event => dispatch(actions.editServiceChangeServiceDescription(event.target.value.toUpperCase()))}
        value={description}
        margin='normal' />
    </FormControl>
    {(tenant && tenant.reportSignature) &&
      <Signature
        isNewData={true}
        editMode={editMode}
      />
    }
    { editMode && <Box sx={styles.buttonsContainer}>
        <Button color='primary' variant='contained' sx={styles.printerButton} onClick={handleSubmit}>
          <Typography>{i18n.t('editService.confirm') as string}</Typography>
        </Button>
    </Box> }
    {uploadSubmitError && <Box sx={styles.fileError}>{i18n.t('divFormService.fileStillUploading') as string}</Box>}
    {fileUploadError && <Box sx={styles.fileError}>{i18n.t('divFormService.fileError') as string}</Box>}
  </Box>
};

const styles = {
  sectionTitleContainer: {
    display: 'flex',
    marginTop: 20,
  },
  fileError: {
    color: '#f44336'
  },
  submitButtons: {
    display: 'flex',
    justifyContent: 'flexEnd',
    padding: '20px 0',
  },
  submitButton: {
    display: 'flex',
    flexDirection: 'column'
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px',
    marginBottom: '70px',
  },
  printerButton: {
    display: 'flex',
    gap: 1,
  },
}

export default Checklist;
